import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LocalizedMessage from '../common/LocalizedMessage';
import ClientLogin from '../manage/Login';
import ContextChoice from '../auth/ContextChoice';
import { ROLES } from '../common/Const';
import * as userActions from '../../redux/actions/usersActions';
import { clearLastSelectedContextKey, rememberLastSelectedContextKey } from '../../services/accountContextStorage';
import ClientPortalShell from './ClientPortalShell';
import ClientSchoolCalendar from './ClientSchoolCalendar';
import calendarContextKey from '../schoolCalendar/calendarContext';
import ClientDashboard from './ClientDashboard';
import ClientPortalSection from './ClientPortalSection';
import LessonCard from './LessonCard';
import useClientPortal, { participationKey, portalScope } from './useClientPortal';
import { parsePortalNavigation, portalSearch } from './clientPortalNavigation';
import { fullName, selectPortal } from './clientPortalSelectors';
import SupportReportsProvider from '../support/SupportReportsProvider';
import SupportRoutes from '../support/SupportRoutes';
import ClientSchedule from './ClientSchedule';
import { schoolToday } from './clientScheduleWindow';

export function ClientPortal({ user, checkToken, getLoginContexts, selectLoginContext, signOut }) {
  const { t: tr } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const canAccess = Boolean(user && user.role === ROLES.CLIENT && user.clientId);
  const isGuest = !user || user.role === ROLES.GUEST;
  const scope = portalScope(user);
  const [billingYear, setBillingYear] = useState('current');
  const billingRequest = new URLSearchParams(location.search).get('section') === 'billing';
  const portal = useClientPortal(scope, canAccess, signOut, billingRequest ? billingYear : undefined);
  const [confirmation, setConfirmation] = useState(null);
  const [switchChoice, setSwitchChoice] = useState(null);
  const [switchError, setSwitchError] = useState('');
  const [switching, setSwitching] = useState(false);
  const [now, setNow] = useState(Date.now());
  const headingRef = useRef(null);
  const shouldFocus = useRef(false);
  const previousScope = useRef({ scope, canAccess });
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const students = portal.snapshot ? portal.snapshot.students || [] : [];
  const today = schoolToday(now);
  const navigation = parsePortalNavigation(location.search, students, { today });
  const isSupport = location.pathname.startsWith('/client/support');
  const isSchoolCalendar = location.pathname === '/client/school-calendar';
  let {section} = navigation;
  if (isSchoolCalendar) section = 'school-calendar';
  if (isSupport) section = 'support';
  const { selectedStudentId, scheduleWindow } = navigation;
  const saving = portal.actionState.status === 'saving' || portal.loadState.status === 'refreshing';
  const busy = saving || switching;
  useEffect(() => { if (isGuest) checkToken(); }, [isGuest, checkToken]);
  useEffect(() => {
    if (confirmation && portal.snapshot && !(portal.snapshot.schedule || []).some(lesson => participationKey(lesson) === confirmation && lesson.canCancel)) setConfirmation(null);
  }, [confirmation, portal.snapshot]);
  useEffect(() => {
    const tick = () => setNow(Date.now());
    const timer = setInterval(tick, 60000);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', tick); };
  }, []);
  useEffect(() => {
    if (previousScope.current.scope !== scope) {
      const resetSelection = previousScope.current.canAccess && canAccess;
      previousScope.current = { scope, canAccess };
      setBillingYear('current');
      setConfirmation(null); setSwitchChoice(null); setSwitchError(''); setSwitching(false);
      if (resetSelection) history.replace('/client');
    }
  }, [scope, canAccess, history]);
  useEffect(() => {
    if (!isSupport && !isSchoolCalendar && portal.snapshot && navigation.normalizedSearch !== location.search) history.replace(`/client${navigation.normalizedSearch}`);
  }, [isSupport, isSchoolCalendar, portal.snapshot, navigation.normalizedSearch, location.search, history]);
  useEffect(() => {
    setConfirmation(null);
    if (shouldFocus.current && headingRef.current) { headingRef.current.focus(); shouldFocus.current = false; }
  }, [section, selectedStudentId, scheduleWindow && scheduleWindow.start, scheduleWindow && scheduleWindow.days]);
  const navigate = (target) => { setConfirmation(null); shouldFocus.current = target !== section; };
  const link = (target, label) => <Link to={`/client${portalSearch(target, selectedStudentId, scheduleWindow)}`} onClick={() => navigate(target)}>{label}</Link>;
  const switchContext = async () => {
    if (busy) return;
    setSwitchError(''); setSwitching(true);
    try {
      const choices = await getLoginContexts();
      if (scopeRef.current === scope) setSwitchChoice(choices);
    } catch (error) {
      if (scopeRef.current === scope) setSwitchError(error.message);
    } finally {
      if (scopeRef.current === scope) setSwitching(false);
    }
  };
  const switchSelect = async (context) => {
    if (busy) return;
    setSwitching(true);
    try {
      const selectedUser = await selectLoginContext(switchChoice.contextChoiceToken, context.contextKey);
      rememberLastSelectedContextKey(switchChoice.accountId, context.contextKey);
      setSwitchChoice(null); setConfirmation(null);
      history.replace(selectedUser.role === ROLES.CLIENT ? '/client' : '/manage');
    } catch (error) { clearLastSelectedContextKey(switchChoice.accountId); setSwitchError(error.message); } finally { setSwitching(false); }
  };
  if (isGuest) return <div className="quiz-page row m-0 p-0 d-flex justify-content-center align-items-center"><div className="col-lg-8 col-11"><ClientLogin /></div></div>;
  if (!canAccess) {
    return (
      <div className="manage-container row m-0 d-flex justify-content-center">
        <div className="col-lg-10 col-11 mt-lg-5 mt-3">
          <div className="alert alert-danger" role="alert">{tr("client:noPermission_8")}</div>
          <div className="d-flex flex-wrap mb-3">
            {[ROLES.ADMIN, ROLES.OPERATOR, ROLES.TEACHER].includes(user.role) && <Link className="btn manage-teal-button mr-2 mb-2" to="/manage">{tr('client:goToManagement')}</Link>}
            {user.canSwitchContext && <button type="button" className="btn manage-teal-button mr-2 mb-2" disabled={busy} onClick={switchContext}>{tr('common:switchAccount')}</button>}
            <button type="button" className="btn manage-teal-button mb-2" disabled={busy} onClick={signOut}>{tr('common:signOut')}</button>
          </div>
          {switchChoice && <ContextChoice contexts={switchChoice.availableContexts || []} onSelect={switchSelect} disabled={busy} error={switchError} onCancel={() => setSwitchChoice(null)} />}
          {switchError && !switchChoice && <div className="alert alert-danger" role="alert"><LocalizedMessage isError value={switchError} /></div>}
        </div>
      </div>
    );
  }
  const {snapshot} = portal;
  const data = snapshot ? selectPortal({ snapshot, selectedStudentId, now }) : null;
  const renderLesson = (lesson, presentation = {}) => {
    const key = participationKey(lesson);
    return (
      <LessonCard
        key={key}
        lesson={lesson}
        variant={presentation.variant}
        showDate={presentation.showDate}
        lessonTypes={snapshot.lessonTypes || []}
        disabled={busy || portal.loadState.status !== 'ready'}
        confirming={confirmation === key}
        onRequest={() => setConfirmation(key)}
        onDismiss={() => setConfirmation(null)}
        onConfirm={async () => { const saved = await portal.cancel(lesson); if (saved && scopeRef.current === scope) setConfirmation(null); }}
        onUndo={() => portal.undo(lesson)}
      />
    );
  };
  let content = null;
  if (!isSchoolCalendar && snapshot) {
    if (section === 'dashboard') {
      content = <ClientDashboard data={data} client={snapshot.client || {}} hasStudents={students.length > 0} lessonTypes={snapshot.lessonTypes || []} renderLesson={renderLesson} link={link} />;
    } else if (section === 'schedule') {
      content = (
        <ClientSchedule
          lessons={data.schedule}
          window={scheduleWindow}
          today={today}
          studentLabel={selectedStudentId ? fullName(students.find(student => student.id === selectedStudentId)) : tr('client:allStudents_52')}
          disabled={busy}
          onWindowChange={next => history.push(`/client${portalSearch('schedule', selectedStudentId, next)}`)}
          renderLesson={renderLesson}
        />
      );
    } else {
      content = <ClientPortalSection section={section} data={data} snapshot={snapshot} selectedStudentId={selectedStudentId} onBillingYear={setBillingYear} />;
    }
  }
  return (
    <SupportReportsProvider user={user}>
      <Helmet><title>{tr("client:extraEnglishClientPortal_9")}</title></Helmet>
      <ClientPortalShell
        section={section}
        students={isSchoolCalendar || isSupport ? [] : students}
        selectedStudentId={isSchoolCalendar ? null : selectedStudentId}
        scheduleWindow={isSchoolCalendar ? null : scheduleWindow}
        busy={busy}
        identity={fullName(snapshot && snapshot.client ? snapshot.client : user)}
        canSwitch={Boolean(user.canSwitchContext)}
        onSwitch={switchContext}
        onSignOut={signOut}
        onNavigate={navigate}
        onStudent={studentId => { setConfirmation(null); history.push(`/client${portalSearch(section, studentId, scheduleWindow)}`); }}
        headingRef={headingRef}
      >
        {switchChoice && <ContextChoice contexts={switchChoice.availableContexts || []} onSelect={switchSelect} disabled={busy} error={switchError} onCancel={() => setSwitchChoice(null)} />}
        {switchError && !switchChoice && <div className="alert alert-danger" role="alert"><LocalizedMessage isError value={switchError} /></div>}
        {isSupport ? <SupportRoutes /> : (
          <>
            {portal.loadState.status === 'loading' && <p role="status">{tr("client:loadingPortal_10")}</p>}
            {portal.loadState.status === 'refreshing' && <p role="status">{tr("client:refreshingDataTheDisplayedDataMayBeOutOf_11")}</p>}
            {portal.actionState.status === 'saving' && <p role="status">{tr("client:savingChange_12")}</p>}
            {portal.actionState.message && (
            <p
              role={portal.actionState.status === 'error' ? 'alert' : 'status'}
              className={`alert alert-${portal.actionState.status === 'error' ? 'danger' : 'success'}`}
            >
              <LocalizedMessage isError={portal.actionState.status === 'error'} value={portal.actionState.message} />
            </p>
            )}
            {['error', 'stale', 'denied'].includes(portal.loadState.status) && (
            <div className="alert alert-warning" role="alert">
              {portal.loadState.status === 'stale' && <p>{tr("client:dataMayBeOutOfDateRefreshBeforeMaking_13")}</p>}
              <p><LocalizedMessage value={portal.loadState.message} /></p>
              {portal.loadState.status !== 'denied' && <button type="button" className="btn manage-teal-button" onClick={portal.refresh}>{tr("client:retryRefresh_14")}</button>}
            </div>
            )}
            {isSchoolCalendar && <ClientSchoolCalendar key={calendarContextKey(user)} contextKey={calendarContextKey(user)} />}
            {content}
          </>
        )}
      </ClientPortalShell>
    </SupportReportsProvider>
  );
}
ClientPortal.propTypes = {
  user: PropTypes.object, checkToken: PropTypes.func.isRequired, getLoginContexts: PropTypes.func, selectLoginContext: PropTypes.func, signOut: PropTypes.func,
};
ClientPortal.defaultProps = { user: null, getLoginContexts: () => Promise.resolve(null), selectLoginContext: () => Promise.resolve(null), signOut: () => {} };
const mapStateToProps = state => ({ user: state.user });
const mapDispatchToProps = { checkToken: userActions.checkToken, getLoginContexts: userActions.getLoginContexts, selectLoginContext: userActions.selectLoginContext, signOut: userActions.signOut };
export default connect(mapStateToProps, mapDispatchToProps)(ClientPortal);
